/**
 * @format
 * @flow strict-local
 */
// DEPENDENCIES

import React, {
	useEffect,
	useReducer,
	useRef,
	useState
} from "react";

import utilsHelper from "../helpers/utils";

import i18n from "../i18n";

// HELPERS

/**
 * reducer - ...
 * 
 * @param {object} state ...
 * @param {object} action ...
 */
const reducer = (state, action) => {
	switch(action.type) {
		case "RESET_ERRORS":
		return {};
		case "RESET_INPUTS":
		return {};
		case "RESET_STATES":
		return {
			changeFound: false,
			isSubmit: false,
			isSubmitted: false,
			isValid: false
		};
		case "SET":
		return action.payload;
		case "UPDATE":
		return {
			...state,
			...action.payload
		};
		default:
		return state;
	}
};

// HOOK

const useApi = () => {
	const [
		inputErrors, 
		dispatchInputErrors
	] = useReducer(
		reducer, 
		{}
	);
	const [
		inputs, 
		dispatchInputs
	] = useReducer(
		reducer, 
		{}
	);
	const [
		inputStates, 
		dispatchInputStates
	] = useReducer(
		reducer, 
		{}
	);

	const inputConfigs = useRef({});

	// Lifecycle

	// Note - Run on bootstrap
	useEffect(
		() => {
			if(utilsHelper.isObjectEmpty(inputs)) {
				return;
			}

			validateForm(true);
		}, 
		[inputs]
	);

	// Events

	/**
	 * eventHandler - ...
	 * 
	 * @param {object} e ...
	 */
	const eventHandler = (e) => {
		let newInputsPayload = {};

		const {
			target: {
				name,
				value
			},
			type
		} = e;

		if(!inputs[name]) {
			return;
		}

		newInputsPayload[name] = Object.assign(
			{}, 
			inputs[name]
		);

		switch(type) {
			case "blur":
				let newConfig = Object.assign(
					{}, 
					inputConfigs.current[name]
				);

				newConfig.touched = true;

				inputConfigs.current[name] = newConfig;
			break;
			case "change":
				switch(inputConfigs.current[name].inputType) {
					default:
						newInputsPayload[name].value = formatInput(
							name, 
							value
						);
					break;
				}
			break;
			case "click":
				switch(inputConfigs.current[name].inputType) {
					/*
					case "optionList":
						//newInputsPayload[name].selectionValue = value;
						newInputsPayload[name].value = value;
					break;
					case "suggestList":
						newInputsPayload[name].selectionValue = value;
						newInputsPayload[name].value = label;
					break;
					case "switch":
						newInputsPayload[name].checked = value;
						newInputsPayload[name].value = inputConfigs.current[name].switchValues[value];
					break;
					*/
					default:
						newInputsPayload[name].value = value;
					break;
				}
			break;
		}

		dispatchInputs({
			type: "UPDATE",
			payload: newInputsPayload
		});
	};

	// Misc

	/**
	 * destroyForm - ...
	 */
	const destroyForm = () => {
		inputConfigs.current = {};

		dispatchInputErrors({
			type: "RESET_ERRORS"
		});
		dispatchInputs({
			type: "RESET_INPUTS"
		});
		dispatchInputStates({
			type: "RESET_STATES"
		});
	};

	/**
	 * formatInput - ...
	 * 
	 * @param {string} inputKey ...
	 * @param {mixed} value ... ??? or string
	 */
	const formatInput = (inputKey, value) => {
		if(!inputConfigs.current[inputKey]) {
			return;
		}

		const formatter = inputConfigs.current[inputKey].formatter;

		switch(formatter) {
			case "digit":
			return (
				value ? 
				utilsHelper.parseStringDigits(value) : 
				value
			);
			case "lowercase":
			return (
				value ? 
				value.toLowerCase() : 
				value
			);
			default:
			return value;
		}
	};

	/**
	 * getFormValues - ...
	 */
	const getFormValues = () => {
		let values = {};

		for(const inputKey of Object.keys(inputs)) {
			values[inputKey] = inputs[inputKey].value;
		};

		return values;
	};

	/**
	 * getInputErrorText - ...
	 * 
	 * @param {string} inputKey ...
	 */
	const getInputErrorText = (inputKey) => {
		if(!inputConfigs.current[inputKey]) {
			return;
		}

		return (
			inputErrors[inputKey] ?
			i18n.t(`inputs.error.${inputConfigs.current[inputKey].inputErrorType}.${inputErrors[inputKey]}`) :
			""
		);
	};	

	/**
	 * hasError - ...
	 * 
	 * @param {string} inputKey ...
	 */
	const hasError = (inputKey) => {
		return (
			inputErrors[inputKey] ?
			true :
			false
		);
	};	

	/**
	 * initForm - ...
	 * 
	 * @param {object} inputs ...
	 */
	const initForm = (inputs) => {
		let newInputConfigs = {};
		let newInputErrorsPayload = {};
		let newInputsPayload = {};

		Object.keys(inputs).map((inputKey) => {
			newInputConfigs[inputKey] = {
				formatter: 
					inputs[inputKey].formatter || 
					""
				,
				initialValue: inputs[inputKey].initialValue,
				inputErrorType: inputs[inputKey].inputErrorType,
				inputType: inputs[inputKey].inputType,
				touched: inputs[inputKey].touched,
				validators: 
					inputs[inputKey].validators || 
					{}
			};

			newInputErrorsPayload[inputKey] = "";

			switch(inputs[inputKey].inputType) {
				case "switch":
					newInputConfigs[inputKey].checkValue = inputs[inputKey].checkValue;
					newInputConfigs[inputKey].switchValues = inputs[inputKey].switchValues;

					newInputsPayload[inputKey] = {
						checked: 
							inputs[inputKey].initialValue === inputs[inputKey].checkValue ? 
							true : 
							false
						,
						value: inputs[inputKey].initialValue
					};
				break;
				default:
					newInputsPayload[inputKey] = {
						value: inputs[inputKey].initialValue
					};
				break;
			}
		});

		inputConfigs.current = newInputConfigs;

		dispatchInputErrors({
			type: "SET",
			payload: newInputErrorsPayload
		});
		dispatchInputs({
			type: "SET",
			payload: newInputsPayload
		});
		dispatchInputStates({
			type: "RESET_STATES"
		});
	};

	/**
	 * parseApiErrors - ...
	 * 
	 * @param {object} errors ...
	 */
	const parseApiErrors = (errors) => {
		errors = 
			utilsHelper.isArray(errors) ? 
			errors : 
			[]
		;

		if(!errors.length) {
			return;
		}

		let isValid = true;
		let newInputErrorsPayload = Object.assign(
			{}, 
			inputErrors
		);

		errors.map((errorItem) => {
			if(newInputErrorsPayload.hasOwnProperty(errorItem.field)) {
				newInputErrorsPayload[errorItem.field] = errorItem.code;

				isValid = false;
			}
		});

		dispatchInputErrors({
			type: "UPDATE",
			payload: newInputErrorsPayload
		});
	};
	/*
	const refreshInputConfigInitialValues = useCallback(() => {
		//console.log("refreshInputConfigInitialValues");
		Object.keys(inputConfigs.current).map((inputKey) => {
			inputConfigs.current[inputKey].initialValue = inputs[inputKey].value;
		});
	});
	*/
	/**
	 * setInputConfigs - ...
	 * 
	 * @param {object} configs ...
	 */
	const setInputConfigs = (configs) => {
		inputConfigs.current = configs;
	};

	/**
	 * setInputErrors - ...
	 * 
	 * @param {object} payload ...
	 */
	const setInputErrors = (payload) => {
		dispatchInputErrors({
			type: "SET",
			payload: payload
		});
	};

	/**
	 * setInputs - ...
	 * 
	 * @param {object} payload ...
	 */
	const setInputs = (payload) => {
		dispatchInputs({
			type: "SET",
			payload: payload
		});
	};

	/**
	 * setInputStates - ...
	 * 
	 * @param {object} payload ...
	 */
	const setInputStates = (payload) => {
		dispatchInputStates({
			type: "SET",
			payload: payload
		});
	};

	/**
	 * updateInputConfigs - ...
	 * 
	 * @param {object} updatedConfigs ...
	 */
	const updateInputConfigs = (updatedConfigs) => {
		inputConfigs.current = {
			...inputConfigs.current,
			...updatedConfigs
		};
	};

	/**
	 * updateInputErrors - ...
	 * 
	 * @param {object} payload ...
	 */
	const updateInputErrors = (payload) => {
		dispatchInputErrors({
			type: "UPDATE",
			payload: payload
		});
	};

	/**
	 * updateInputs - ...
	 * 
	 * @param {object} payload ...
	 */
	const updateInputs = (payload) => {
		dispatchInputs({
			type: "UPDATE",
			payload: payload
		});
	};

	/**
	 * updateInputStates - ...
	 * 
	 * @param {object} payload ...
	 */
	const updateInputStates = (payload) => {
		dispatchInputStates({
			type: "UPDATE",
			payload: payload
		});
	};

	/**
	 * validateForm - ...
	 * 
	 * @param {boolean} checkTouched ...
	 */	
	const validateForm = (checkTouched = false) => {
		console.log("validateForm");
		let changeFound = false;
		let isValid = true;
		let newInputErrorsPayload = Object.assign(
			{}, 
			inputErrors
		);

		Object.keys(inputConfigs.current).map((inputKey) => {
			const {
				validators
			} = inputConfigs.current[inputKey];

			if(!utilsHelper.isObjectEmpty(validators)) {
				const error = validateInput(inputKey);

				if(error) {
					isValid = false;
				}

				if(
					inputConfigs.current[inputKey].touched || 
					!checkTouched
				) {
					newInputErrorsPayload[inputKey] = error;
				}
			}

			if(!changeFound) {
				switch(inputConfigs.current[inputKey].inputType) {
					case "phoneNumber":
						if(
							inputs[inputKey].value.countryCode !== 
							inputConfigs.current[inputKey].initialValue.countryCode
						) {
							changeFound = true;
						} else if(
							inputs[inputKey].value.number !== 
							inputConfigs.current[inputKey].initialValue.number
						) {
							changeFound = true;
						}
					break;
					default:
						if(
							inputs[inputKey].value !== 
							inputConfigs.current[inputKey].initialValue
						) {
							changeFound = true;
						}
					break;
				}
			}
		});

		dispatchInputErrors({
			type: "UPDATE",
			payload: newInputErrorsPayload
		});
		dispatchInputStates({
			type: "UPDATE",
			payload: {
				changeFound: changeFound,
				isValid: isValid
			}
		});
	};

	/**
	 * validateInput - ...
	 * 
	 * @param {string} inputKey ...
	 */
	const validateInput = (inputKey) => {
		console.log("validateInput");
		if(!inputs[inputKey]) {
			return;
		}

		const {
			inputType,
			validators
		} = inputConfigs.current[inputKey];
		const {
			value
		} = inputs[inputKey];

		if(utilsHelper.isObjectEmpty(validators)) {
			return;
		}

		let error = "";

		for(const validatorKey of Object.keys(validators)) {
			if(validators[validatorKey] !== false) {
				switch(validatorKey) {
					case "custom":
						if(!value) {
							error = validators[validatorKey];
						}
					break;
					case "email":
					case "password":
					case "verificationCode":
						if(value) {
							error = 
								utilsHelper.validationPatterns[validatorKey].test(value) ? 
								"" : 
								"pattern"
							;
						}
					break;
					case "mustMatch":
						const matchValue = inputs[validators[validatorKey]].value;

						if(value !== matchValue) {
							error = validatorKey;
						}
					break;
					case "required":
						if(!value) {
							error = validatorKey;
						}
					break;
					case "stripeCard":
						if(!value.complete) {
							if(!utilsHelper.isObjectEmpty(value.error)) {
								error = value.error.code;
							} else {
								error = "incomplete";
							}
						}
					break;
				}
			}

			if(error) {
				break;
			}
		}

		return error;
	};

	// Return

	return {
		destroyForm,
		eventHandler,
		formatInput,
		getFormValues,
		getInputErrorText,
		hasError,
		initForm,
		inputConfigs,
		inputErrors,
		inputs,
		inputStates,
		parseApiErrors,
		//parseChangedValues,
		//refreshInputConfigInitialValues,
		setInputConfigs,
		setInputErrors,
		setInputs,
		setInputStates,
		updateInputConfigs,
		updateInputErrors,
		updateInputs,
		updateInputStates,
		validateForm
	};
};

// EXPORT

export default useApi;
